/**
 * L'aperçu d'un fichier dans la conversation du tuteur (`common/messenger/MediaPreviewModal.js` et son
 * état dans `CommonChat.js`) : la part pure, exportée et testée.
 *
 * Deux entrées, comme le legacy :
 *  - une pièce de l'élève sans activité (« Aperçu ») : une photo ouvre l'aperçu sur toutes les photos
 *    du fil, du plus ancien au plus récent, à son rang (`chatImageFiles`, `currentImageIndex`) ; un PDF
 *    s'ouvre seul, sans flèches ;
 *  - un fichier que le tuteur vient de choisir (« Nouveau fichier ») : seul, et il peut partir.
 * Plus la visionneuse plein écran d'une photo de l'aperçu (le zoom du legacy y est passé).
 */
import { type TutorChatMedia } from "../../../../adapters/tutor/Students/tutorStudentConversationAdapter"
import { type UIConversationMessage } from "../../../types/studentConversation"
import { chatMediaOf } from "../../../../adapters/tutor/Students/tutorStudentConversationAdapter"

export type TutorMediaPreview = {
    media: TutorChatMedia
    fresh: boolean
    /** Les photos du fil qu'on parcourt (une pièce de l'élève seulement). */
    images: TutorChatMedia[]
    index: number
}

export type TutorMediaPreviewState = {
    preview: TutorMediaPreview | null
    /** « Envoyer dans le chat » est parti. */
    sending: boolean
    /** La visionneuse plein écran de la photo montrée. */
    viewerOpen: boolean
}

export type TutorMediaPreviewAction =
    | { type: "OPEN_EXISTING"; message: UIConversationMessage; messages: UIConversationMessage[] }
    | { type: "OPEN_FRESH"; media: TutorChatMedia }
    | { type: "NAVIGATE"; index: number }
    | { type: "SENDING"; sending: boolean }
    | { type: "OPEN_VIEWER" }
    | { type: "CLOSE_VIEWER" }
    | { type: "CLOSE" }

export const initialTutorMediaPreviewState: TutorMediaPreviewState = {
    preview: null,
    sending: false,
    viewerOpen: false,
}

/** Un envoi d'ici (`local-…`) n'est pas encore un message du serveur : rien à quoi rattacher l'activité. */
const LOCAL_PREFIX = "local-"
const serverMessageId = (id: string) => (id.startsWith(LOCAL_PREFIX) ? null : id)

/** Une pièce du fil → l'aperçu, rattaché à son message s'il vient du serveur. */
function mediaOfMessage(message: UIConversationMessage): TutorChatMedia | null {
    const media = chatMediaOf(message.id, message.attachment)
    return media == null ? null : { ...media, messageId: serverMessageId(message.id) }
}

export function tutorMediaPreviewReducer(
    state: TutorMediaPreviewState,
    action: TutorMediaPreviewAction
): TutorMediaPreviewState {
    switch (action.type) {
        case "OPEN_EXISTING": {
            const media = mediaOfMessage(action.message)
            if (media == null) return state
            if (media.kind === "document") {
                return { ...initialTutorMediaPreviewState, preview: { media, fresh: false, images: [], index: 0 } }
            }
            const images = action.messages
                .filter((message) => message.attachment?.kind === "image")
                .map(mediaOfMessage)
                .filter((item): item is TutorChatMedia => item != null)
            const index = Math.max(
                0,
                images.findIndex((item) => item.uri === media.uri && item.messageId === media.messageId)
            )
            return {
                ...initialTutorMediaPreviewState,
                preview: {
                    media: images[index] ?? media,
                    fresh: false,
                    images: images.length > 0 ? images : [media],
                    index,
                },
            }
        }
        case "OPEN_FRESH":
            return {
                ...initialTutorMediaPreviewState,
                preview: { media: action.media, fresh: true, images: [], index: 0 },
            }
        case "NAVIGATE": {
            const preview = state.preview
            const next = preview?.images[action.index]
            if (preview == null || next == null) return state
            return { ...state, preview: { ...preview, media: next, index: action.index } }
        }
        case "SENDING":
            return { ...state, sending: action.sending }
        case "OPEN_VIEWER":
            return state.preview?.media.kind === "image" ? { ...state, viewerOpen: true } : state
        case "CLOSE_VIEWER":
            return { ...state, viewerOpen: false }
        case "CLOSE":
            return initialTutorMediaPreviewState
        default:
            return state
    }
}
